import { ShipmentFiltersPayload } from '@/pages/Shipments/ControlTower/types';
import { AIStateKey } from '../components/AIStateFilter';
import { SentimentKey } from '../components/SentimentFilter';
import { CommunicationTypeKey } from '../components/TypeFilter';

export type CustomRange = { start: string; end: string; period?: string };

export const parseCustomRange = (
  val: string | null | undefined
): CustomRange | null => {
  if (!val || typeof val !== 'string') return null;
  const s = val.trim();
  if (!s.startsWith('{') || !s.endsWith('}')) return null;

  try {
    const parsed = JSON.parse(s) as Partial<CustomRange>;
    if (parsed?.start && parsed?.end) {
      return {
        start: parsed.start,
        end: parsed.end,
        ...(parsed.period ? { period: parsed.period } : {}),
      };
    }
  } catch {
    return null;
  }

  return null;
};

export interface HistoryPayloadArgs {
  filtersPayload: ShipmentFiltersPayload | null;
  search: string;
  period: string | null;
  aiState?: AIStateKey[];
  sentiment?: SentimentKey[];
  communicationType?: CommunicationTypeKey[];
}

export interface HistoryPayload {
  [key: string]: unknown;
  search: string;
  date_range: string;
  custom_range: CustomRange | null;
  ai_state?: AIStateKey[];
  sentiment?: SentimentKey[];
  communication_type?: CommunicationTypeKey[];
}

// `period` is a preset key or a JSON-encoded custom range from the calendar.
export const buildHistoryPayload = ({
  filtersPayload,
  search,
  period,
  aiState,
  sentiment,
  communicationType,
}: HistoryPayloadArgs): HistoryPayload => {
  const custom = parseCustomRange(period);
  const payload: HistoryPayload = {
    ...(filtersPayload ?? {}),
    search,
    date_range: custom ? '' : (period ?? ''),
    custom_range: custom,
  };

  if (aiState?.length) payload.ai_state = aiState;
  if (sentiment?.length) payload.sentiment = sentiment;
  if (communicationType?.length) payload.communication_type = communicationType;
  return payload;
};

// Counts are the AI state/sentiment facets, so they ignore their own selections.
export const toCountsPayload = ({
  ai_state: _aiState,
  sentiment: _sentiment,
  ...rest
}: HistoryPayload): HistoryPayload => ({
  ...rest,
  search: (rest.search || '').trim(),
});
